// AdminImageOverlay, wraps any image in an editorial article with an inline
// edit control visible only when an admin is logged in.
//
// Usage:
//   <AdminImageOverlay blockId={block._blockId} fieldPath="src" currentSrc={block.src} onUpdated={setSrc}>
//     <img src={block.src} ... />
//   </AdminImageOverlay>
//
// fieldPath supports dot notation: "src", "imageA.src", "imageB.src"
// wrapperStyle, optional styles merged onto the wrapper div (e.g. for grid items)

import { useState, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { supabase } from '../../lib/supabaseClient';
import { storageUpload } from '../../utils/storageUpload';

const GOLD  = '#c9a96e';
const DARK  = '#18160f';
const FU    = "'Nunito','Inter',sans-serif";

// Set a nested value by dot-notation path
// Clone preserving arrays (needed for array-indexed paths like "images.0.src")
function shallowClone(v) {
  return Array.isArray(v) ? [...v] : (v && typeof v === 'object' ? { ...v } : v);
}

function setPath(obj, path, value) {
  const keys = path.split('.');
  const result = shallowClone(obj) || {};
  let cur = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];
    // Missing node: create array for numeric next key, object otherwise
    cur[key] = cur[key] == null
      ? (/^\d+$/.test(nextKey) ? [] : {})
      : shallowClone(cur[key]);
    cur = cur[key];
  }
  cur[keys[keys.length - 1]] = value;
  return result;
}

export default function AdminImageOverlay({
  blockId, fieldPath, currentSrc, onUpdated, children, wrapperStyle,
}) {
  const { isAuthenticated } = useAdminAuth();
  const [hovered,   setHovered]   = useState(false);
  const [open,      setOpen]      = useState(false);
  const [url,       setUrl]       = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const fileRef = useRef(null);

  // Public site: no edit affordances, but the layout wrapper MUST still apply.
  // wrapperStyle carries the frame (e.g. aspectRatio for dual-image cells); dropping
  // it here let portrait images run to full natural height instead of holding their frame.
  if (!isAuthenticated) {
    return wrapperStyle ? <div style={wrapperStyle}>{children}</div> : children;
  }

  const save = async (newUrl) => {
    if (!newUrl?.trim() || !blockId) return;
    setSaving(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('magazine_blocks')
        .select('block_content')
        .eq('id', blockId)
        .single();
      if (fetchErr) throw fetchErr;

      const updated = setPath(data.block_content || {}, fieldPath, newUrl.trim());

      const { error: saveErr } = await supabase
        .from('magazine_blocks')
        .update({ block_content: updated })
        .eq('id', blockId);
      if (saveErr) throw saveErr;

      onUpdated?.(newUrl.trim());
      setOpen(false);
      setUrl('');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setError('');
    try {
      const { url: uploadedUrl } = await storageUpload(file, 'magazine-pages');
      await save(uploadedUrl);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Single wrapper div, position:relative so the absolute edit button and
  // panel are contained. wrapperStyle lets callers adjust layout (e.g. a
  // grid item needs width:100% or explicit sizing passed from the parent).
  return (
    <div
      style={{ position: 'relative', ...wrapperStyle }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!open) setHovered(false); }}
    >
      {children}

      {/* Edit button, appears on hover or while panel is open */}
      {(hovered || open) && (
        <button
          onClick={e => { e.stopPropagation(); setOpen(o => !o); setUrl(currentSrc || ''); setError(''); }}
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(18,14,8,0.85)', border: `1px solid ${GOLD}55`,
            color: GOLD, borderRadius: 3, padding: '5px 10px',
            fontFamily: FU, fontSize: 9, fontWeight: 700,
            letterSpacing: '0.15em', textTransform: 'uppercase',
            cursor: 'pointer', backdropFilter: 'blur(4px)', zIndex: 10,
          }}
        >
          ✎ Edit Image
        </button>
      )}

      {/* Inline edit panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 44, right: 10, zIndex: 20,
          background: DARK, border: `1px solid ${GOLD}33`,
          borderRadius: 4, padding: 16, width: 280,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
        }}>
          <div style={{ fontFamily: FU, fontSize: 9, color: GOLD, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 10 }}>
            Replace Image
          </div>

          {/* URL input */}
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && save(url)}
            placeholder="Paste image URL..."
            style={{
              width: '100%', boxSizing: 'border-box',
              background: '#0f0e0a', border: `1px solid ${GOLD}33`,
              borderRadius: 2, color: '#f5f0e8', fontFamily: FU,
              fontSize: 12, padding: '8px 10px', outline: 'none',
              marginBottom: 8,
            }}
          />

          {/* Actions row */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              onClick={() => save(url)}
              disabled={saving || !url.trim()}
              style={{
                flex: 1, fontFamily: FU, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.15em', textTransform: 'uppercase',
                background: GOLD, color: '#1a1806', border: 'none',
                borderRadius: 2, padding: '8px 0', cursor: 'pointer',
                opacity: saving || !url.trim() ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>

            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              style={{
                fontFamily: FU, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                background: 'transparent', color: GOLD,
                border: `1px solid ${GOLD}55`, borderRadius: 2,
                padding: '8px 12px', cursor: 'pointer',
                opacity: uploading ? 0.5 : 1,
              }}
            >
              {uploading ? '↑' : '↑ Upload'}
            </button>

            <button
              onClick={() => { setOpen(false); setHovered(false); setError(''); }}
              style={{
                fontFamily: FU, fontSize: 9, color: 'rgba(245,240,232,0.4)',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '8px 6px',
              }}
            >
              ✕
            </button>
          </div>

          {error && (
            <div style={{ fontFamily: FU, fontSize: 10, color: '#e06', marginTop: 8 }}>
              {error}
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
        </div>
      )}
    </div>
  );
}
